/* /assets/landing-page.js */
/* Bella Landra Landing Page JavaScript */

(function() {
  'use strict';

  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    initializeLandingPage();
  });

  function initializeLandingPage() {
    // Initialize all components
    initStickyHeader();
    initSmoothScrolling();
    initCartFunctionality();
    initScrollTracking();
    initExitIntent();
    initIntersectionObserver();
    initCountdownTimer();
    initRecentOrdersTicker();
    initStockCounter();
    initPerformanceTracking();
    initErrorHandling();
  }

  // Sticky Header Management
  function initStickyHeader() {
    const stickyCTA = document.querySelector('.sticky-cta');
    const packagesSection = document.getElementById('packages');
    const hero = document.querySelector('.landing-hero');
    
    if (!stickyCTA || !packagesSection) return;

    function handleStickyHeader() {
      const heroBottom = hero ? hero.offsetTop + hero.offsetHeight : 0;
      const packagesSectionTop = packagesSection.offsetTop;
      const packagesSectionBottom = packagesSectionTop + packagesSection.offsetHeight;
      const scrollPosition = window.pageYOffset;
      const windowHeight = window.innerHeight;
      
      // Show sticky CTA when past hero but not in packages section
      if (scrollPosition > heroBottom && 
          (scrollPosition + windowHeight < packagesSectionTop || 
           scrollPosition > packagesSectionBottom)) {
        stickyCTA.classList.add('show');
      } else {
        stickyCTA.classList.remove('show');
      }
    }
    
    // Throttle scroll events for performance
    let ticking = false;
    function throttledScrollHandler() {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleStickyHeader();
          ticking = false;
        });
        ticking = true;
      }
    }
    
    window.addEventListener('scroll', throttledScrollHandler);
    handleStickyHeader(); // Initial check
  }

  // Smooth Scrolling for Anchor Links
  function initSmoothScrolling() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        e.preventDefault();
        
        const targetId = this.getAttribute('href');
        const target = document.querySelector(targetId);
        
        if (target) {
          const header = document.querySelector('.landing-header');
          const headerHeight = header ? header.offsetHeight : 0;
          const targetPosition = target.offsetTop - headerHeight - 20;
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
          
          // Track smooth scroll events
          trackEvent('smooth_scroll', {
            target: targetId,
            category: 'Navigation'
          });
        }
      });
    });
  }

  // Enhanced Cart Functionality
  function initCartFunctionality() {
    const addToCartForms = document.querySelectorAll('.add-to-cart-form');
    
    addToCartForms.forEach(form => {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        handleAddToCart(form);
      });
    });
  }

  function handleAddToCart(form) {
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity')) || 1;
    const button = form.querySelector('.package-cta, .add-to-cart-btn');
    const packageCard = form.closest('.package-card');
    const packageName = button.getAttribute('data-package-name') || 
                       packageCard?.querySelector('.package-title')?.textContent || 
                       'Unknown Package';
    
    if (!variantId) {
      showNotification('Error: Product variant not found', 'error');
      return;
    }
    
    // Update button state
    updateButtonState(button, 'loading');
    
    // Add to cart via Shopify AJAX API
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity,
        properties: {
          '_source': 'landing_page',
          '_package': packageName
        }
      })
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(err => Promise.reject(err));
      }
      return response.json();
    })
    .then(data => {
      // Track successful add to cart
      trackAddToCart(variantId, packageName, data.price, quantity);
      
      // Update button to success state
      updateButtonState(button, 'success');
      
      // Show success notification
      showNotification(`${packageName} added to cart!`, 'success');
      
      // Redirect to checkout after delay
      setTimeout(() => {
        window.location.href = '/checkout';
      }, 1500);
    })
    .catch(error => {
      console.error('Add to cart error:', error);
      
      // Reset button state
      updateButtonState(button, 'error');
      
      // Show error notification
      const errorMessage = error.description || error.message || 'Failed to add item to cart';
      showNotification(errorMessage, 'error');
      
      // Track error
      trackEvent('add_to_cart_error', {
        error_message: errorMessage,
        variant_id: variantId,
        package_name: packageName
      });
      
      // Reset button after delay
      setTimeout(() => {
        updateButtonState(button, 'default');
      }, 3000);
    });
  }

  function updateButtonState(button, state) {
    const originalText = button.getAttribute('data-original-text') || button.innerHTML;
    
    if (!button.getAttribute('data-original-text')) {
      button.setAttribute('data-original-text', originalText);
    }
    
    switch (state) {
      case 'loading':
        button.innerHTML = 'Adding to Cart...';
        button.disabled = true;
        button.style.opacity = '0.7';
        button.classList.add('loading');
        break;
        
      case 'success':
        button.innerHTML = '✓ Added! Redirecting...';
        button.style.background = 'linear-gradient(135deg, #28a745 0%, #20c997 100%)';
        button.disabled = true;
        button.classList.remove('loading');
        break;
        
      case 'error':
        button.innerHTML = 'Try Again';
        button.style.background = 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)';
        button.disabled = false;
        button.style.opacity = '1';
        button.classList.remove('loading');
        break;
        
      case 'default':
      default:
        button.innerHTML = originalText;
        button.disabled = false;
        button.style.opacity = '1';
        button.style.background = '';
        button.classList.remove('loading');
        break;
    }
  }

  function showNotification(message, type = 'info') {
    // Remove existing notifications
    const existingNotifications = document.querySelectorAll('.landing-notification');
    existingNotifications.forEach(notification => notification.remove());
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `landing-notification landing-notification--${type}`;
    notification.innerHTML = `
      <div class="landing-notification__content">
        <span class="landing-notification__message">${message}</span>
        <button class="landing-notification__close" onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;
    
    // Add styles
    notification.style.cssText = `
      position: fixed;
      top: 100px;
      right: 20px;
      background: ${type === 'success' ? '#28a745' : type === 'error' ? '#dc3545' : '#007bff'};
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      z-index: 10000;
      max-width: 350px;
      animation: slideInRight 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => notification.remove(), 300);
      }
    }, 5000);
  }

  // Scroll Depth Tracking
  function initScrollTracking() {
    let maxScrollDepth = 0;
    const scrollDepths = [25, 50, 75, 90, 100];
    const scrollDepthTriggered = {};
    
    function trackScrollDepth() {
      const scrollHeight = document.body.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);
      maxScrollDepth = Math.max(maxScrollDepth, scrollPercent);
      
      scrollDepths.forEach(depth => {
        if (scrollPercent >= depth && !scrollDepthTriggered[depth]) {
          scrollDepthTriggered[depth] = true;
          
          trackEvent('scroll_depth', {
            depth: depth,
            category: 'Engagement'
          });
        }
      });
    }
    
    // Throttle scroll events
    let scrollTicking = false;
    function throttledScrollDepthHandler() {
      if (!scrollTicking) {
        requestAnimationFrame(() => {
          trackScrollDepth();
          scrollTicking = false;
        });
        scrollTicking = true;
      }
    }
    
    window.addEventListener('scroll', throttledScrollDepthHandler);
  }

  // Exit Intent Detection
  function initExitIntent() {
    let exitIntentTriggered = false;
    
    document.addEventListener('mouseleave', function(e) {
      if (e.clientY <= 0 && !exitIntentTriggered) {
        exitIntentTriggered = true;
        
        trackEvent('exit_intent', {
          category: 'Engagement',
          time_on_page: Math.round((Date.now() - window.pageLoadTime) / 1000)
        });
        
        // Show exit intent popup (optional)
        // showExitIntentOffer();
      }
    });
    
    // Track page load time
    window.pageLoadTime = Date.now();
  }

  // Intersection Observer for Animations
  function initIntersectionObserver() {
    if (!window.IntersectionObserver) return;
    
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          
          // Track section view
          const sectionName = entry.target.id || entry.target.className.split(' ')[0];
          trackEvent('section_view', {
            section: sectionName,
            category: 'Engagement'
          });
        }
      });
    }, observerOptions);
    
    // Observe all major sections
    document.querySelectorAll('.landing-section, .problem-card, .result-card, .tech-card, .package-card').forEach(el => {
      observer.observe(el);
    });
  }

  // Countdown Timer
  function initCountdownTimer() {
    const countdownDisplay = document.getElementById('countdown-display');
    if (!countdownDisplay) return;
    
    // Get countdown duration from data attribute or default to 24 hours
    const countdownHours = parseInt(countdownDisplay.getAttribute('data-hours')) || 24;
    const endTime = new Date().getTime() + (countdownHours * 60 * 60 * 1000);
    
    function updateCountdown() {
      const now = new Date().getTime();
      const distance = endTime - now;
      
      if (distance > 0) {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        
        countdownDisplay.innerHTML = 
          String(days).padStart(2, '0') + ':' +
          String(hours).padStart(2, '0') + ':' +
          String(minutes).padStart(2, '0') + ':' +
          String(seconds).padStart(2, '0');
      } else {
        countdownDisplay.innerHTML = "00:00:00:00";
        // Optionally hide the countdown or show expired message
      }
    }
    
    updateCountdown();
    const countdownInterval = setInterval(updateCountdown, 1000);
    
    // Clear interval when page is hidden to save resources
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        clearInterval(countdownInterval);
      } else {
        // Restart countdown when page becomes visible
        initCountdownTimer();
      }
    });
  }

  // Recent Orders Ticker
  function initRecentOrdersTicker() {
    const recentOrdersElement = document.getElementById('recent-orders-ticker');
    if (!recentOrdersElement) return;
    
    const locations = [
      'New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX', 'Phoenix, AZ',
      'Philadelphia, PA', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'San Jose, CA',
      'Austin, TX', 'Jacksonville, FL', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC',
      'San Francisco, CA', 'Indianapolis, IN', 'Seattle, WA', 'Denver, CO', 'Washington, DC',
      'Boston, MA', 'Portland, OR', 'Las Vegas, NV', 'Nashville, TN', 'Detroit, MI',
      'Atlanta, GA', 'Miami, FL', 'Tampa, FL', 'Orlando, FL', 'Minneapolis, MN'
    ];
    
    const packages = ['Starter Package', 'His & Hers Package', 'Family Package'];
    const timeAgo = ['2 minutes ago', '5 minutes ago', '8 minutes ago', '12 minutes ago', '15 minutes ago', '18 minutes ago'];
    
    function generateRecentOrder() {
      const location = locations[Math.floor(Math.random() * locations.length)];
      const packageType = packages[Math.floor(Math.random() * packages.length)];
      const time = timeAgo[Math.floor(Math.random() * timeAgo.length)];
      
      return `📦 Someone in ${location} ordered the ${packageType} ${time}`;
    }
    
    function updateRecentOrders() {
      const orders = [];
      for (let i = 0; i < 3; i++) {
        orders.push(generateRecentOrder());
      }
      
      recentOrdersElement.innerHTML = orders.join('<br>');
    }
    
    updateRecentOrders();
    setInterval(updateRecentOrders, 8000);
  }

  // Stock Counter Animation
  function initStockCounter() {
    const stockFill = document.querySelector('.stock-fill');
    if (!stockFill) return;
    
    const targetWidth = parseInt(stockFill.style.width) || 23;
    let currentWidth = 0;
    
    function animateStockBar() {
      const increment = targetWidth / 50; // 50 steps for smooth animation
      
      const interval = setInterval(() => {
        currentWidth += increment;
        if (currentWidth >= targetWidth) {
          currentWidth = targetWidth;
          clearInterval(interval);
        }
        stockFill.style.width = currentWidth + '%';
      }, 20);
    }
    
    // Trigger animation when stock counter is in view
    const stockCounter = document.querySelector('.stock-counter');
    if (stockCounter && window.IntersectionObserver) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            animateStockBar();
            observer.unobserve(entry.target);
          }
        });
      });
      
      observer.observe(stockCounter);
    }
  }

  // Performance Tracking
  function initPerformanceTracking() {
    window.addEventListener('load', function() {
      setTimeout(() => {
        if (window.performance && window.performance.timing) {
          const timing = window.performance.timing;
          const loadTime = timing.loadEventEnd - timing.navigationStart;
          const domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
          
          trackEvent('page_performance', {
            load_time: Math.round(loadTime / 1000),
            dom_content_loaded: Math.round(domContentLoaded / 1000),
            category: 'Performance'
          });
        }
      }, 0);
    });
  }

  // Error Handling
  function initErrorHandling() {
    window.addEventListener('error', function(e) {
      trackEvent('javascript_error', {
        message: e.message,
        filename: e.filename,
        line: e.lineno,
        category: 'Error'
      });
    });
    
    window.addEventListener('unhandledrejection', function(e) {
      trackEvent('promise_rejection', {
        reason: e.reason,
        category: 'Error'
      });
    });
  }

  // Conversion Tracking Helper
  function trackAddToCart(variantId, packageName, price, quantity = 1) {
    const eventData = {
      variant_id: variantId,
      package_name: packageName,
      price: price / 100, // Convert cents to dollars
      quantity: quantity,
      currency: 'USD',
      source: 'landing_page'
    };
    
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'add_to_cart', {
        currency: 'USD',
        value: eventData.price,
        items: [{
          item_id: variantId,
          item_name: packageName,
          item_category: 'Anti-Aging Device',
          quantity: quantity,
          price: eventData.price
        }]
      });
    }
    
    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'AddToCart', {
        content_ids: [variantId],
        content_name: packageName,
        content_type: 'product',
        value: eventData.price,
        currency: 'USD'
      });
    }
    
    // Klaviyo
    if (typeof klaviyo !== 'undefined') {
      klaviyo.track('Added to Cart', {
        'Product Name': packageName,
        'Product Price': eventData.price,
        'Currency': 'USD',
        'Source': 'Landing Page Package Selection',
        'Variant ID': variantId
      });
    }
    
    // TikTok Pixel
    if (typeof ttq !== 'undefined') {
      ttq.track('AddToCart', {
        content_id: variantId,
        content_name: packageName,
        content_type: 'product',
        value: eventData.price,
        currency: 'USD'
      });
    }
    
    // Custom tracking
    trackEvent('add_to_cart_success', eventData);
  }

  // Generic Event Tracking
  function trackEvent(eventName, eventData = {}) {
    // Console log for debugging
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
      console.log('Track Event:', eventName, eventData);
    }
    
    // Send to Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, eventData);
    }
    
    // Send to Facebook
    if (typeof fbq !== 'undefined' && eventName !== 'add_to_cart_success') {
      fbq('trackCustom', eventName, eventData);
    }
    
    // Send to Klaviyo
    if (typeof klaviyo !== 'undefined') {
      klaviyo.track(eventName, eventData);
    }
  }

  // Utility Functions
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }

  // Add required CSS for notifications
  const notificationStyles = document.createElement('style');
  notificationStyles.textContent = `
    @keyframes slideInRight {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
    
    @keyframes slideOutRight {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(100%);
        opacity: 0;
      }
    }
    
    .landing-notification__content {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    
    .landing-notification__close {
      background: none;
      border: none;
      color: white;
      font-size: 1.2rem;
      cursor: pointer;
      margin-left: 10px;
      padding: 0;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .landing-notification__close:hover {
      opacity: 0.8;
    }
  `;
  document.head.appendChild(notificationStyles);

  // Export functions for external use if needed
  window.BellaLandraLanding = {
    trackEvent: trackEvent,
    showNotification: showNotification,
    trackAddToCart: trackAddToCart
  };

})();